import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { SetupStep } from "@/components/SetupProgressTracker";
import { isAuthenticated } from "@/lib/auth";
import { getApnsCertInfo } from "@/services/tenantsApi";
import { getAllConfigurations } from "@/services/adeApi";
import { listPolicies } from "@/services/policiesApi";
import { listDevices } from "@/services/devicesApi";

const DISMISS_KEY = "setup_progress_dismissed";

const STEP_DEFS: Array<Omit<SetupStep, "completed">> = [
  { id: "apns", name: "Configure Apple\nPush Notification Service", path: "/settings?category=apple" },
  { id: "abm", name: "Setup Apple\nBusiness Manager", path: "/settings?category=apple" },
  { id: "profiles", name: "Create\nConfiguration Profiles", path: "/configurations" },
  { id: "devices", name: "Enroll\nDevices", path: "/devices" },
];

/** Treat any thrown / network error as "not configured" so the tracker degrades gracefully. */
async function safe<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch {
    return fallback;
  }
}

export function useSetupProgress() {
  const loggedIn = isAuthenticated();

  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === "true";
    } catch {
      return false;
    }
  });

  // APNS — completed when the cert info call returns a non-empty object.
  const apnsQuery = useQuery({
    queryKey: ["setup-progress", "apns"],
    enabled: loggedIn,
    queryFn: () => safe(getApnsCertInfo, null as unknown),
    staleTime: 60_000,
  });

  // ABM — completed when at least one ADE configuration exists.
  const abmQuery = useQuery({
    queryKey: ["setup-progress", "abm"],
    enabled: loggedIn,
    queryFn: () => safe(() => getAllConfigurations(), [] as unknown[]),
    staleTime: 60_000,
  });

  // Configuration profiles — completed when at least one policy exists.
  const profilesQuery = useQuery({
    queryKey: ["setup-progress", "profiles"],
    enabled: loggedIn,
    queryFn: () => safe(() => listPolicies(), [] as unknown[]),
    staleTime: 60_000,
  });

  // Devices — completed when at least one device is returned (page size 1 is enough).
  const devicesQuery = useQuery({
    queryKey: ["setup-progress", "devices"],
    enabled: loggedIn,
    queryFn: () => safe(() => listDevices(0, 1), null as unknown),
    staleTime: 60_000,
  });

  const apnsDone = useMemo(() => {
    const v = apnsQuery.data;
    return !!v && typeof v === "object" && Object.keys(v as Record<string, unknown>).length > 0;
  }, [apnsQuery.data]);

  const abmDone = useMemo(() => {
    const v = abmQuery.data;
    return Array.isArray(v) && v.length > 0;
  }, [abmQuery.data]);

  const profilesDone = useMemo(() => {
    const v = profilesQuery.data;
    return Array.isArray(v) && v.length > 0;
  }, [profilesQuery.data]);

  const devicesDone = useMemo(() => {
    const res = devicesQuery.data as { data?: unknown } | unknown[] | null | undefined;
    if (!res) return false;
    if (Array.isArray(res)) return res.length > 0;
    const data = (res as { data?: unknown }).data;
    if (Array.isArray(data)) return data.length > 0;
    if (data && typeof data === "object" && Array.isArray((data as { content?: unknown[] }).content)) {
      return ((data as { content: unknown[] }).content).length > 0;
    }
    return false;
  }, [devicesQuery.data]);

  const setupSteps: SetupStep[] = useMemo(
    () =>
      STEP_DEFS.map((s) => ({
        ...s,
        completed:
          s.id === "apns"
            ? apnsDone
            : s.id === "abm"
              ? abmDone
              : s.id === "profiles"
                ? profilesDone
                : devicesDone,
      })),
    [apnsDone, abmDone, profilesDone, devicesDone],
  );

  const isLoading =
    loggedIn &&
    (apnsQuery.isLoading || abmQuery.isLoading || profilesQuery.isLoading || devicesQuery.isLoading);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, "true");
    } catch {
      /* ignore */
    }
    setDismissed(true);
  }, []);

  // Keep a no-op for legacy callers that toggled steps manually.
  const updateStepCompletion = useCallback((_id: string, _completed: boolean) => {
    /* deprecated: status is now derived from the API */
  }, []);

  // React to manual changes to the dismiss key (e.g. multiple tabs).
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === DISMISS_KEY) setDismissed(e.newValue === "true");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return {
    setupSteps,
    isLoading,
    isLoggedIn: loggedIn,
    dismissed,
    dismiss,
    updateStepCompletion,
  };
}
